import { useState, useEffect, useCallback } from 'react';
import { 
  generateMockOrders, 
  filterOrders, 
  sortOrders, 
  calculateMetrics,
  getOrdersByTab as getOrdersByTabUtil,
  exportOrdersToCSV,
  downloadFile
} from './liveOrdersUtils';
import { decryptData } from '../../../utils/storageCrypto';

const useLiveOrders = () => {
  // State management
  const [rawData, setRawData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(true);
  const [metrics, setMetrics] = useState({});
  
  // Filters and preferences
  const [filters, setFilters] = useState({
    searchTerm: '',
    statusFilter: 'all',
    priorityFilter: 'all',
    dateFrom: null,
    dateTo: null,
    amountFrom: null,
    amountTo: null,
    sortBy: 'date',
    sortOrder: 'desc'
  });
  
  const [viewPreferences, setViewPreferences] = useState({
    itemsPerPage: 25,
    autoRefresh: true,
    refreshInterval: 30000,
    showMetrics: true,
    compactView: false
  });

  // Transform backend data to frontend format
  const transformBackendData = useCallback((backendData) => {
    if (!backendData || !backendData.data) {
      return [];
    }

    const { quotes = [], allQuickBuyVendorResponses = [], SalesOrders = [], purchaseOrders = [],suppliers = [],customers = [] } = backendData.data;
    const transformedOrders = [];

    // Transform quotes to orders
    quotes.forEach((quote, index) => {
      // Find related vendor responses
      const relatedResponses = allQuickBuyVendorResponses.filter(
        response => response.quotation_id === quote._id
      );

      // Find related sales orders
      const relatedSalesOrders = SalesOrders.filter(
        salesOrder => salesOrder.quotation_id === quote._id
      );

      // Find related purchase orders
      const relatedPurchaseOrders = purchaseOrders.filter(po => 
        po.items && po.items.some(item => 
          item.salesorder_ids && relatedSalesOrders.some(so => 
            item.salesorder_ids.includes(so._id)
          )
        )
      );

      // Determine order status based on the flow
      let orderStatus = 'pending';
      let lastInteraction = quote.quotation_date || quote.createdAt || new Date();
      
      if (relatedResponses.length > 0) {
        orderStatus = 'quotation_sent';
        const latestResponse = relatedResponses.reduce((latest, current) => 
          new Date(current.createdAt || current.response_date) > new Date(latest.createdAt || latest.response_date) 
            ? current : latest
        );
        lastInteraction = latestResponse.createdAt || latestResponse.response_date || lastInteraction;
      }

      if (relatedSalesOrders.length > 0) {
        orderStatus = 'confirmed';
        const latestSalesOrder = relatedSalesOrders.reduce((latest, current) => 
          new Date(current.createdAt || current.salesorder_date) > new Date(latest.createdAt || latest.salesorder_date) 
            ? current : latest
        );
        lastInteraction = latestSalesOrder.createdAt || latestSalesOrder.salesorder_date || lastInteraction;
        
        // Check sales order status
        if (latestSalesOrder.status === 'Approved') {
          orderStatus = 'processing';
        }
      }

      if (relatedPurchaseOrders.length > 0) {
        orderStatus = 'processing';
        const latestPurchaseOrder = relatedPurchaseOrders.reduce((latest, current) => 
          new Date(current.createdAt || current.purchase_order_date) > new Date(latest.createdAt || latest.purchase_order_date) 
            ? current : latest
        );
        lastInteraction = latestPurchaseOrder.createdAt || latestPurchaseOrder.purchase_order_date || lastInteraction;
        
        // Check purchase order status
        if (latestPurchaseOrder.status === 'Approved') {
          orderStatus = 'shipped';
        }
        if (latestPurchaseOrder.purchaseorder_status === 'Dispatched') {
          orderStatus = 'delivered';
        }
      }

      // Calculate total amount
      let totalAmount = 0;
      if (quote.total) {
        totalAmount = quote.total;
      } else if (quote.items && quote.items.length > 0) {
        totalAmount = quote.items.reduce((sum, item) => sum + (item.total || 0), 0);
      }

      // Get customer and supplier info
      const customerInfo = quote.customer_id || {};
      const supplierInfo = relatedResponses.length > 0 && relatedResponses[0].status?.toLowerCase() === "awarded" 
        ? relatedResponses[0].vendor_id || {} 
        : {};

      const transformedOrder = {
        id: quote.quotation_number || `ORD-${String(index + 1).padStart(4, '0')}`,
        quotationId: quote._id,
        rfq_number : quote?.rfq?.rfq_number || '',
        customerName: customerInfo.customer_name || customerInfo.name || `Customer ${index + 1}`,
        customerEmail: customerInfo.customer_email || customerInfo.email || `customer${index + 1}@example.com`,
        customerPhone: customerInfo.customer_phone || customerInfo.phone || customerInfo.mobile_number /* || '+1-555-0000' */,
        customerAddress: customerInfo.customer_address || customerInfo.address || 'Address not available',
        supplierName: supplierInfo.vendor_name || supplierInfo.name || `Order Not Awarded to any supplier`,
        supplierEmail: supplierInfo.vendor_email || supplierInfo.email || ``,
        supplierPhone: supplierInfo.vendor_phone || supplierInfo.phone || supplierInfo.mobile_number /* || '+1-555-0000' */,
        supplierAddress: supplierInfo.vendor_address || supplierInfo.address || 'Address not available',
        status: quote.status,
        priority: quote.priority || 'medium',
        orderDate: quote.quotation_date || quote.createdAt || new Date(),
        tax_amount: quote.tax_amount || 0,
        subtotal: quote.subtotal || 0,
        total: quote.total || 0,
        lastInteraction: lastInteraction,
        expectedDelivery: quote.expiry_date || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
        specialInstructions: quote.terms_and_conditions || null,
        deliveryNotes: null,
        paymentTerms: 'Net 30',
        shippingMethod: 'Standard Shipping',
        items: quote.items || [],
        notifications:quote.notifications || [],
        // Additional data for reference
        rawQuote: quote,
        vendorResponses: relatedResponses,
        salesOrders: relatedSalesOrders,
        purchaseOrders: relatedPurchaseOrders,
        customers:customers || [],
        suppliers:suppliers || [],
        updated_at: quote.updated_at,
        order_waiting_time:quote.order_waiting_time || 10,
        created_at: quote.created_at,
        image:quote.files[0] || null,
        // Mock communications for now
        communications: [
          {
            type: 'email',
            timestamp: lastInteraction,
            subject: 'Order Status Update',
            message: `Order status updated to ${orderStatus}`,
            user: 'System'
          }
        ]
      };

      transformedOrders.push(transformedOrder);
    });

    return transformedOrders;
  }, []);

// Fetch live order status
const fetchLiveOrderStatus = useCallback(async () => {
  try {
    setLoading(true);
    setError(null);
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);

    // Use your original endpoint
    const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}customer/quotations/get-live-order-status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-token': token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Live order status fetched:', data);

    if (data.success) {
      setRawData(data);
      const transformedOrders = transformBackendData(data);
      setOrders(transformedOrders);
      console.log('Order status updated:', data.data);
      setIsConnected(true);
    } else {
      throw new Error(data.message || 'Failed to fetch live order status');
    }
  } catch (error) {
    console.error('Error fetching live order status:', error);
    setError(error.message);
    setIsConnected(false);
    
    // Fallback to mock data for development
/*     if (process.env.NODE_ENV === 'development') {
      const mockOrders = generateMockOrders(10);
      setOrders(mockOrders);
    } */
  } finally {
    setLoading(false);
  }
}, [transformBackendData]);


  // Apply filters and sorting
  useEffect(() => {
    let filtered = filterOrders(orders, filters);
    filtered = sortOrders(filtered, filters.sortBy, filters.sortOrder);
    setFilteredOrders(filtered);
    
    // Calculate metrics
    const newMetrics = calculateMetrics(filtered);
    setMetrics(newMetrics);
  }, [orders, filters]);

  // Auto refresh
  useEffect(() => {
    fetchLiveOrderStatus();
    
    let intervalId;
    if (viewPreferences.autoRefresh) {
      intervalId = setInterval(fetchLiveOrderStatus, viewPreferences.refreshInterval);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [fetchLiveOrderStatus, viewPreferences.autoRefresh, viewPreferences.refreshInterval]);

  // API functions
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const updateViewPreferences = useCallback((newPreferences) => {
    setViewPreferences(prev => ({ ...prev, ...newPreferences }));
  }, []);

  const refresh = useCallback(() => {
    fetchLiveOrderStatus();
  }, [fetchLiveOrderStatus]);

  const search = useCallback((searchTerm) => {
    updateFilters({ searchTerm });
  }, [updateFilters]);

  const setStatusFilter = useCallback((statusFilter) => {
    updateFilters({ statusFilter });
  }, [updateFilters]);

  const exportOrders = useCallback((format = 'csv') => {
    try {
      if (format === 'csv') {
        const csvContent = exportOrdersToCSV(filteredOrders);
        downloadFile(csvContent, `orders-${new Date().toISOString().split('T')[0]}.csv`);
        return { success: true };
      }
      return { success: false, error: 'Unsupported format' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [filteredOrders]);

  const getOrdersByTab = useCallback((tabIndex) => {
    return getOrdersByTabUtil(filteredOrders, tabIndex);
  }, [filteredOrders]);

  const updateOrderStatus = useCallback(async (orderId, newStatus) => {
    try {
      // Find the order
      const order = orders.find(o => o.id === orderId);
      if (!order) {
        throw new Error('Order not found');
      }

      // API call to update status
      const response = await fetch(`/api/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          // Add authentication headers if needed
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Update local state
      setOrders(prev => prev.map(o => 
        o.id === orderId 
          ? { ...o, status: newStatus, lastInteraction: new Date() }
          : o
      ));

      return { success: true };
    } catch (error) {
      console.error('Error updating order status:', error);
      return { success: false, error: error.message };
    }
  }, [orders]);

  const sendEmail = useCallback(async (orderId, emailData) => {
    try {
      const response = await fetch(`/api/orders/${orderId}/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add authentication headers if needed
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return { success: true };
    } catch (error) {
      console.error('Error sending email:', error);
      return { success: false, error: error.message };
    }
  }, []);

  return {
    // Data
    orders,
    filteredOrders,
    metrics,
    rawData,
    
    // State
    loading,
    error,
    isConnected,
    
    // Filters and preferences
    filters,
    viewPreferences,
    
    // Actions
    updateFilters,
    updateViewPreferences,
    refresh,
    search,
    setStatusFilter,
    exportOrders,
    getOrdersByTab,
    updateOrderStatus,
    sendEmail
  };
};

export default useLiveOrders;
